import React, { createContext, useContext, useEffect, useMemo, useState, useRef } from 'react';

const CartContext = createContext();

// Shopping cart context provider
export const CartProvider = ({ children }) => {
  const STORAGE_KEY = 'cart';

  const safeRead = () => {
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const [cart, setCart] = useState(() => safeRead());
  const [notification, setNotification] = useState(null);
  const notificationTimeoutRef = useRef(null);

  const showNotification = (message) => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    setNotification(message);
    notificationTimeoutRef.current = setTimeout(() => {
      setNotification(null);
      notificationTimeoutRef.current = null;
    }, 3000);
  };

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
    } catch {
      // ignore storage errors
    }
  }, [cart]);

  const addToCart = (item) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find(
        (cartItem) => cartItem.id === item.id && cartItem.size === item.size && cartItem.comment === item.comment
      );
      if (existingItem) {
        return prevCart.map((cartItem) =>
          cartItem.id === item.id && cartItem.size === item.size && cartItem.comment === item.comment
            ? { ...cartItem, quantity: cartItem.quantity + item.quantity }
            : cartItem
        );
      }
      return [...prevCart, item];
    });
    showNotification(`${item.name} added to cart!`);
  };

  const removeFromCart = (itemId, size, comment) => {
    setCart((prevCart) =>
      prevCart.filter((item) => !(item.id === itemId && item.size === size && item.comment === comment))
    );
  };

  const updateQuantity = (itemId, size, comment, newQuantity) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === itemId && item.size === size && item.comment === comment
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };
  const updateComment = (itemId, size, oldComment, newComment) => {
      setCart((prevCart) => {
          const itemToUpdate = prevCart.find(
              (item) => item.id === itemId && item.size === size && item.comment === oldComment
          );
          if (!itemToUpdate) return prevCart;

          const existingItem = prevCart.find(
              (item) => item.id === itemId && item.size === size && item.comment === newComment
          );

          if (existingItem && oldComment !== newComment) {
              return prevCart.filter(item => item !== itemToUpdate).map(item =>
                  item.id === itemId && item.size === size && item.comment === newComment
                      ? { ...item, quantity: item.quantity + itemToUpdate.quantity }
                      : item
              );
          } else {
              return prevCart.map((item) =>
                  item.id === itemId && item.size === size && item.comment === oldComment
                      ? { ...item, comment: newComment }
                      : item
              );
          }
      });
  };

  const clearCart = () => {
    setCart([]);
  };

  const cartValue = useMemo(
    () => ({
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      updateComment,
      clearCart,
      notification,
      setNotification,
    }),
    [cart, notification]
  );

  return <CartContext.Provider value={cartValue}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
